/**
 * Health store module
 * Manages service health monitoring state
 */
import { defineStore } from 'pinia'
import { healthApi } from '@/services/api'

export const useHealthStore = defineStore('health', {
  state: () => ({
    services: [],
    lastUpdated: null,
    loading: false,
    error: null,
    autoRefreshInterval: null,
    criticalServicesDown: []
  }),

  getters: {
    healthySevices: (state) => state.services.filter(s => s.status === 'healthy'),
    degradedServices: (state) => state.services.filter(s => s.status === 'degraded'),
    downServices: (state) => state.services.filter(s => s.status === 'down'),
    
    servicesByStatus: (state) => {
      return {
        healthy: state.services.filter(s => s.status === 'healthy').length,
        degraded: state.services.filter(s => s.status === 'degraded').length,
        down: state.services.filter(s => s.status === 'down').length
      }
    },
    
    overallHealth: (state) => {
      const total = state.services.length
      if (total === 0) return 'unknown'
      
      const down = state.downServices.length
      const degraded = state.degradedServices.length
      
      if (down > 0) return 'critical'
      if (degraded > total / 2) return 'warning'
      if (degraded > 0) return 'partial'
      return 'healthy'
    }
  },

  actions: {
    async fetchServiceHealth() {
      this.loading = true
      this.error = null
      try {
        const response = await healthApi.getServiceHealth()
        this.services = response.data.services || []
        this.lastUpdated = new Date(response.data.last_updated)
        
        // Check for critical services
        this.criticalServicesDown = this.services
          .filter(s => s.status === 'down' && this.isCriticalService(s.name))
          .map(s => s.name)
          
      } catch (error) {
        this.error = error.message || 'Failed to fetch service health'
        console.error('Health fetch error:', error)
      } finally {
        this.loading = false
      }
    },

    async getServiceDetails(serviceName) {
      try {
        const response = await healthApi.getServiceDetails(serviceName)
        return response.data
      } catch (error) {
        this.error = error.message || 'Failed to fetch service details'
        throw error
      }
    },

    startAutoRefresh(intervalMs = 30000) {
      this.stopAutoRefresh()
      this.autoRefreshInterval = setInterval(() => {
        this.fetchServiceHealth()
      }, intervalMs)
    },

    stopAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval)
        this.autoRefreshInterval = null
      }
    },

    isCriticalService(serviceName) {
      const criticalServices = [
        'mcp_orchestrator',
        'auth_service',
        'notification_service',
        'medication_data_service'
      ]
      return criticalServices.includes(serviceName)
    },

    getServiceStatusColor(status) {
      switch (status) {
        case 'healthy':
          return 'success'
        case 'degraded':
          return 'warning'
        case 'down':
          return 'error'
        default:
          return 'grey'
      }
    },

    getResponseTimeColor(responseTime) {
      if (!responseTime) return 'grey'
      if (responseTime < 100) return 'success'
      if (responseTime < 500) return 'warning'
      return 'error'
    }
  }
})